import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Table2,
  Download,
  ExternalLink,
  Plus,
  Unlink,
  Copy,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import Papa from "papaparse";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  listAllReferenceTables,
  listVersionReferenceTables,
  linkReferenceTableToVersion,
  unlinkReferenceTableFromVersion,
  duplicateReferenceTable,
} from "@/lib/api/references";
import type { ReferenceTable, ReferenceTableWithVersions } from "@/types";

interface ReferencesTabProps {
  versionId: number;
  isEditable: boolean;
}

export function ReferencesTab({ versionId, isEditable }: ReferencesTabProps) {
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [selectedTable, setSelectedTable] = useState<ReferenceTable | null>(null);
  const queryClient = useQueryClient();

  const { data: linkedTables, isLoading } = useQuery({
    queryKey: ["version-reference-tables", versionId],
    queryFn: () => listVersionReferenceTables(versionId),
    enabled: !!versionId,
  });

  const { data: allTablesData } = useQuery({
    queryKey: ["references", "all"],
    queryFn: () => listAllReferenceTables(),
    enabled: linkDialogOpen,
  });

  const linkMutation = useMutation({
    mutationFn: (referenceTableId: number) =>
      linkReferenceTableToVersion(versionId, referenceTableId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["version-reference-tables", versionId] });
      toast.success("Table de référence associée");
    },
    onError: () => {
      toast.error("Erreur lors de l'association");
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (referenceTableId: number) =>
      unlinkReferenceTableFromVersion(versionId, referenceTableId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["version-reference-tables", versionId] });
      toast.success("Table de référence dissociée");
    },
    onError: () => {
      toast.error("Erreur lors de la dissociation");
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (referenceTableId: number) =>
      duplicateReferenceTable(referenceTableId),
    onSuccess: (newTable) => {
      queryClient.invalidateQueries({ queryKey: ["references"] });
      queryClient.invalidateQueries({ queryKey: ["version-reference-tables", versionId] });
      toast.success(`Table "${newTable.name}" créée — pensez à l'associer à cette version`);
    },
    onError: () => {
      toast.error("Erreur lors de la duplication");
    },
  });

  const linkedIds = new Set((linkedTables ?? []).map((t) => t.id));

  const availableTables = useMemo(() => {
    const tables = (allTablesData?.items ?? []).filter(
      (t) => !linkedIds.has(t.id)
    );
    if (!linkSearch.trim()) return tables;
    const q = linkSearch.toLowerCase();
    return tables.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        (t.description && t.description.toLowerCase().includes(q))
    );
  }, [allTablesData?.items, linkedIds, linkSearch]);

  const groupedAvailableTables = useMemo(() => {
    const groups: Record<string, ReferenceTableWithVersions[]> = {};
    for (const table of availableTables) {
      if (!groups[table.slug]) groups[table.slug] = [];
      groups[table.slug].push(table as ReferenceTableWithVersions);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [availableTables]);

  const formatLinkedVersions = (table: ReferenceTableWithVersions) => {
    const versions = table.linked_versions ?? [];
    if (versions.length === 0) return null;
    const grouped = versions.reduce<Record<string, number[]>>((acc, v) => {
      if (!acc[v.simulator_name]) acc[v.simulator_name] = [];
      acc[v.simulator_name].push(v.version_number);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([name, nums]) => `${name} v${nums.sort((a, b) => a - b).join(", v")}`)
      .join(" · ");
  };

  const handleView = (table: ReferenceTable) => {
    setSelectedTable(table);
    setViewDialogOpen(true);
  };

  const handleExport = (table: ReferenceTable) => {
    if (!table.data || table.data.length === 0) {
      toast.error("Aucune donnée à exporter");
      return;
    }

    const csv = Papa.unparse(table.data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `${table.slug}.csv`;
    anchor.click();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const tables = linkedTables ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Tables de référence</CardTitle>
          <CardDescription>
            Tables de référence associées à cette version
            {tables.length > 0 && (
              <span className="ml-1">({tables.length} table{tables.length > 1 ? "s" : ""})</span>
            )}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {isEditable && (
            <Button variant="outline" onClick={() => setLinkDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Associer une table
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/admin/references">
              <ExternalLink className="mr-2 h-4 w-4" />
              Gérer les tables
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {tables.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Table2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucune table de référence associée</p>
            <p className="text-sm mt-2">
              {isEditable
                ? "Cliquez sur \"Associer une table\" pour lier des tables de référence à cette version."
                : "Les tables de référence apparaîtront ici lorsqu'elles seront associées à cette version."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {tables.map((table) => (
              <div
                key={table.id}
                className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-info/10 flex items-center justify-center">
                    <Table2 className="h-5 w-5 text-info" />
                  </div>
                  <div>
                    <p className="font-medium">{table.name}</p>
                    {table.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1 max-w-md">
                        {table.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <code className="px-1 py-0.5 bg-muted rounded text-xs">
                        {table.slug}
                      </code>
                      <span>•</span>
                      <span>{table.data?.length || 0} lignes</span>
                      <span>•</span>
                      <span>{table.columns?.length || 0} colonnes</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleView(table)}>
                    Voir
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleExport(table)}>
                    <Download className="h-4 w-4" />
                  </Button>
                  {isEditable && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => duplicateMutation.mutate(table.id)}
                        disabled={duplicateMutation.isPending}
                        title="Dupliquer cette table"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unlinkMutation.mutate(table.id)}
                        disabled={unlinkMutation.isPending}
                        title="Dissocier cette table"
                      >
                        <Unlink className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedTable?.name}</DialogTitle>
            <DialogDescription>
              {selectedTable?.data?.length || 0} lignes
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {selectedTable?.data && selectedTable.data.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    {selectedTable.columns?.map((col) => (
                      <TableHead key={col.name}>{col.name}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTable.data.slice(0, 100).map((row, i) => (
                    <TableRow key={i}>
                      {selectedTable.columns?.map((col) => (
                        <TableCell key={col.name}>
                          {String(row[col.name] ?? "")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center py-8 text-muted-foreground">
                Aucune donnée
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={(open) => {
        setLinkDialogOpen(open);
        if (!open) setLinkSearch("");
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Associer une table de référence</DialogTitle>
            <DialogDescription>
              Sélectionnez une table de référence à associer à cette version du simulateur.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, slug ou description..."
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex-1 overflow-auto">
            {availableTables.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                {linkSearch
                  ? "Aucune table ne correspond à la recherche."
                  : "Toutes les tables de référence sont déjà associées."}
              </p>
            ) : (
              <div className="space-y-4">
                {groupedAvailableTables.map(([slug, slugTables]) => (
                  <div key={slug}>
                    {groupedAvailableTables.length > 1 && slugTables.length > 1 && (
                      <div className="flex items-center gap-2 mb-2">
                        <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-medium">
                          {slug}
                        </code>
                        <Badge variant="outline" className="text-xs">
                          {slugTables.length} versions
                        </Badge>
                      </div>
                    )}
                    <div className="space-y-2">
                      {slugTables.map((table) => {
                        const versionsSummary = formatLinkedVersions(table);
                        return (
                          <div
                            key={table.id}
                            className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                          >
                            <div className="min-w-0 flex-1 mr-3">
                              <p className="font-medium text-sm">{table.name}</p>
                              {table.description && (
                                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                  {table.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                <code className="px-1 py-0.5 bg-muted rounded">
                                  {table.slug}
                                </code>
                                <span>•</span>
                                <span>{table.data?.length || 0} lignes</span>
                                {versionsSummary && (
                                  <>
                                    <span>•</span>
                                    <span className="truncate" title={versionsSummary}>
                                      {versionsSummary}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                linkMutation.mutate(table.id);
                              }}
                              disabled={linkMutation.isPending}
                              className="shrink-0"
                            >
                              <Plus className="mr-1 h-3 w-3" />
                              Associer
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
